import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { RouteComponentProps, Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { LocalItem } from '~Types';
import { generateID } from '~reducers';
import * as actions from '~actions';
import { useDispatch, useI18n, useSelector, useInterval } from '~Hooks';
import { useEntryFinder } from '~Components/EntryFinder';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import Welcome from './Scenes/Welcome';
import General from './Scenes/General';
import useExpressQuery from './Components/useExpressQuery';
import Receipt from './Scenes/Receipt';
import LegalAge from './Scenes/LegalAge';
import Timeout from './Scenes/Timeout';
import OrderIdManager from './Components/OrderIdManager';
import OrderIdMaster from './Components/OrderIdMaster';
import BongMaster from '~Scenes/Tables/Components/BongMaster';
import MenuManager from './Components/MenuManager';
import Checkout from './Scenes/Checkout';
import './index.css';
import { ORDER_ORIGIN, SERVICE_TYPE } from '../../../__queries__';
import Onboarding from '~Scenes/Onboarding';
import useOpeningHours from '~Hooks/useOpeningHours';
import InitialLoader from './Components/InitialLoader';
import NoData from './Components/NoData';
import { useItemsPackager } from '~Utils/packagedItems';

dayjs.extend(isBetween);

const Wrapper = styled.div``;

interface MatchParams {
  id: string;
}

export const isItemHaveMSymbol = (title: string) => {
  return title.includes('[m]');
};

export const companiesWithTakeAwayBag = [
  {
    // Zocalo DK
    companyId: 'clv6nuey94udl0b78yrf7rdj2',
    itemId: 'cm8g3503klrw80817h5qv5gh6',
  },
];

type Props = RouteComponentProps<MatchParams>;

const SelfServiceKiosks = ({ match }: Props) => {
  const venueId = match.params.id;
  const { data, loading, progress, progressText } = useExpressQuery(venueId);
  const [legalChoice, setLegalChoice] = useState<string>('');
  const [legalCheck, setLegalCheck] = useState<boolean>(false);
  const [venueOpened, setVenueOpened] = useState<boolean | undefined>(undefined);
  const [receiptOption, setReceiptOption] = useState<string>('');
  const [checkout, setCheckout] = useState<boolean>(false);
  const [items, setItems] = useState<LocalItem[]>([]);
  const [serviceType, setServiceType] = useState<SERVICE_TYPE | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [showOrderNumber, setShowOrderNumber] = useState<boolean>(false);
  const { today, checkOpeningHoursForDate } = useOpeningHours();
  const { i18n } = useI18n();

  const dispatch = useDispatch();
  const findEntryFromItem = useEntryFinder();
  const payment = useSelector((state) => state.POSPayments.find((p) => p.order === orderId));
  const packagedItems = useItemsPackager(items, venueId, null, null);
  const companyId = useSelector((state) => state.company);
  const menu = useSelector((state) => state.POSMenu);

  const order = useSelector((state) => state.POSOrders.find((o) => o.id === orderId));
  const hasPayment = useSelector((state) => state.POSPayments.some((o) => o.order === orderId));

  const { categories = [] } = data?.company || {};
  const filteredCategories = useMemo(
    () =>
      categories
        .map((category) => {
          return {
            ...category,
            // Hide items that contain [m] in the title
            items: category.items.filter((i) => !isItemHaveMSymbol(i18n(i.titleLang))),
          };
        })
        .filter((cat) => !cat.deleted && cat.items.some((item) => findEntryFromItem(item.id)))
        .sort((a, b) => b.order - a.order),
    [categories, menu],
  );

  const defaultOrderStatus = data?.company.defaultOrderStatus;

  const reset = () => {
    setItems([]);
    setLegalChoice('');
    setLegalCheck(false);
    setReceiptOption('');
    setCheckout(false);
    setServiceType(null);
    setShowOrderNumber(false);
  };

  // Limits the items in the cart to 50
  useEffect(() => {
    setItems(items.slice(0, 50));
  }, [items.length]);

  // When the order has been created, add items to the order and
  // create a payment right away
  useEffect(() => {
    if (!order || !menu) {
      return;
    }

    packagedItems.forEach((item) => {
      dispatch(actions.addItemToOrder(order.id, item));
    });
  }, [order && order.id]);

  useEffect(() => {
    if (!orderId) {
      return;
    }

    if (!order && !menu) {
      /**Reinitiate flags when no order exists */
      reset();
    }
  }, [order, menu, orderId]);

  useEffect(() => {
    if (legalChoice === 'yes') {
      setLegalCheck(false);
    } else if (legalChoice === 'no') {
      setLegalChoice('');
      setLegalCheck(false);
      setCheckout(false);
      /**Removes all items with legal age restrictions from order */
      setItems((state) => {
        return state.filter((item) => !item.ageRestricted);
      });
    }
  }, [legalChoice]);

  // Create order once we are at checkout
  useEffect(() => {
    if (checkout && !legalCheck) {
      dispatch(
        // @ts-ignore
        actions.initiateOrder({
          serviceType: serviceType!,
          id: orderId!,
          origin: ORDER_ORIGIN.EXPRESS,
          defaultOrderStatus,
        }),
      );
    }
  }, [checkout, legalCheck, serviceType, defaultOrderStatus]);

  const checkLegalAgeAtCheckout = (checking: boolean) => {
    setCheckout(true);
    setLegalCheck(checking);
  };

  const reloadOrderId = () => setOrderId(generateID());

  const handleStartOrder = (service: SERVICE_TYPE) => {
    setItems([]);
    setServiceType(service);
    reloadOrderId();
  };

  const handleFailedPayment = () => {
    dispatch({
      type: 'REMOVE_POS_PAYMENT',
      id: payment!.id,
    });
    dispatch({
      type: 'REMOVE_POS_ORDER',
      id: order!.id,
    });
    reloadOrderId();
    setCheckout(false);
    setLegalCheck(false);
    setLegalChoice('');
    setReceiptOption('');
  };

  const checkVenueAvailability = () => {
    const result = checkOpeningHoursForDate(new Date());
    setVenueOpened(result.isOpen);
  };

  useEffect(() => {
    setVenueOpened(today.isOpen);
  }, [today]);

  // Check venue if venue is open every minute
  useInterval(checkVenueAvailability, 60000);

  if (loading || !companyId) {
    return <InitialLoader progressText={progressText} progress={progress} />;
  }

  if (!data) {
    return <NoData />;
  }

  const hasStarted = !!serviceType && menu;

  return (
    <React.StrictMode>
      <Wrapper>
        <Welcome visible={!hasStarted} onStart={handleStartOrder} venueOpened={venueOpened} />

        {hasStarted && (
          <General
            categories={filteredCategories!}
            items={items}
            setItems={setItems}
            orderId={orderId}
            venueId={''}
            checkLegalAgeAtCheckout={checkLegalAgeAtCheckout}
            reset={reset}
            serviceType={serviceType}
          />
        )}

        {legalCheck && <LegalAge setLegalChoice={setLegalChoice} />}

        {checkout && !legalCheck && (
          <Receipt orderId={orderId!} setReceiptOption={setReceiptOption} />
        )}

        <Checkout
          onFailPayment={handleFailedPayment}
          payment={payment}
          setShowOrderNumber={setShowOrderNumber}
        />

        {hasStarted && orderId && !showOrderNumber && (
          <Timeout orderId={orderId} reset={reset} hasPayment={hasPayment} />
        )}

        {order && !!order.items.length && (
          <OrderIdManager
            showOrderNumber={showOrderNumber}
            visible={!!receiptOption}
            venueId={venueId}
            orderId={orderId!}
            reset={reset}
            cartItems={order.items}
          />
        )}

        <OrderIdMaster />
        <BongMaster />
        <MenuManager serviceType={serviceType} />
        <Onboarding />
      </Wrapper>
    </React.StrictMode>
  );
};

export default SelfServiceKiosks;
